import React, { useCallback, useEffect, useMemo, useState } from "react";
import toastr from "toastr";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory from "react-bootstrap-table2-paginator";
import ToolkitProvider, {
  Search,
} from "react-bootstrap-table2-toolkit/dist/react-bootstrap-table2-toolkit";
import { confirmAlert } from "react-confirm-alert";
import { Link } from "react-router-dom";
import { FiEdit3, FiEye, FiPlusCircle } from "react-icons/fi";
import { FaRegFileExcel, FaRegTrashAlt } from "react-icons/fa";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import TableExport from "app/views/custom/components/Export/TableExport";
import CustomTooltip from "../CommanComponents/CustomTooltip";
import ApproveReject from "app/views/custom/components/ApproveReject/ApproveReject";
import { SetPermission } from "../SetPermission/Permissions";
import { useDebounce } from "app/_helpers/hooks";
import { IoIosArrowDropdown } from "react-icons/io";
import { CustomToggleList } from "../BootstrapTable/SearchBootstrapTable";
import { Button } from "react-bootstrap";
import { BsFileEarmarkArrowDown } from "react-icons/bs";

const { SearchBar } = Search;

export const TableWithServerSide = ({
  title = "Title",
  columns,
  downloadPermission,
  searchFlag,
  selectRow,
  tableName,
  className,
  apis,
  blockIpButton,
  addLink,
  editLink,
  viewLink,
  makeUserCondition,
  onClickRow,
  tableToolbarView,
  tableDownloadFlag = false,
  fullData = false,
  hideSidColumn = false,
  hideActionsColumn = false,
  hideEditButton = false,
  hideViewButton = false,
  hideDeleteButton = false,
  withApproveReject = false,
  excelExport = false,
  tabRole,
  tableBeforeSearch,
  searchFormKeys,
  hideAddButton,
  tableKey,
}) => {
  const [state, setState] = useState(() => ({
    rows: [],
    total: 0,
    pageSize: 10,
    pageNumber: 1,
    sortOrder: "",
    sortField: "",
    search: "",
    tabRole: tabRole,
    searchFormKeys: searchFormKeys,
  }));

  useEffect(() => {
    setState((prev) => ({
      ...prev,
      tabRole: tabRole,
      searchFormKeys: searchFormKeys,
      pageNumber: 1,
    }));
  }, [searchFormKeys, tabRole]);

  const onChangePage = useCallback(function (pageNumber) {
    setState((prevState) => ({ ...prevState, pageNumber }));
  }, []);
  const onChangePageSize = useCallback(function (pageSize) {
    setState((prevState) => ({ ...prevState, pageSize }));
  }, []);
  const onChangeSearch = useCallback(
    (search) => setState((prevState) => ({ ...prevState, search, pageNumber: 1 })),
    []
  );

  const handleAfterApproveReject = useCallback((status, id) => {
    toastr.success("Success.");
    setState((prev) => ({
      ...prev,
      rows: prev.rows.map((row) => (row.id !== id ? row : { ...row, status })),
    }));
  }, []);

  const handleDeleteRow = useCallback(
    (row) => {
      confirmAlert({
        title: `Delete ${title}`,
        message: "Do you want to proceed?",
        buttons: [
          {
            label: "Yes",
            onClick: async () => {
              const response = await apis?.delete?.api(row.id);

              if (!response?.status)
                return toastr.error(response?.message || "Something went wrong!");

              toastr.info(response.message || "Deleted Successfully!");

              setState((prevState) => ({
                ...prevState,
                rows: prevState.rows.filter((r) => r.id !== row.id),
                total: prevState.total - 1,
              }));
            },
          },
          {
            label: "No",
            onClick: () => {},
          },
        ],
      });
    },
    [apis?.delete, title]
  );

  const handleTableChange = useCallback((type, { sortField, sortOrder }) => {
    sortOrder = sortOrder?.toUpperCase() || "DESC";

    setState((prev) =>
      sortField === "sid"
        ? {
            ...prev,
            rows: prev.rows.sort((a, b) => {
              if (sortOrder === "ASC") return a.sid > b.sid ? -1 : 1;
              else return a.sid > b.sid ? 1 : -1;
            }),
          }
        : { ...prev, sortOrder, sortField }
    );
  }, []);

  // const filterableColumns = useMemo(
  //   () =>
  //     columns
  //       .filter((col) => !col.notFilterable)
  //       .map((col) => ({
  //         filterName: col.filterName || col.dataField,
  //         filterExact: !!col.filterExact,
  //       })),
  //   [columns]
  // );

  const fetchRows = useDebounce(
    async (
      getAll,
      { pageSize, pageNumber, sortOrder, sortField, search, tabRole, searchForm }
    ) => {
      if (!getAll?.api) return;

      search = encodeURIComponent(search.trim());
      const sort = {};

      if (sortField) sort[sortField] = sortOrder;

      const requestPayload = {
        ...(getAll.body || {}),
        pageSize,
        pageNumber,
        sort,
      };

      if (tabRole) requestPayload.tabRole = tabRole;
      if (searchForm) requestPayload.searchForm = searchForm;

      if (search) {
        requestPayload.q = search;
        //   requestPayload.filter = {};
        //   filterableColumns.forEach((column) => {
        //     requestPayload.filter[column.filterName] = column.filterExact
        //       ? search
        //       : `%${search}%`;
        //   });
      }

      const response = await getAll.api(requestPayload);

      if (!Array.isArray(response?.data)) {
        if (process.env.NODE_ENV === "development") console.log("Response:--", response);
        return;
      }

      const count = response.total < pageSize ? response.total : pageSize;
      const start = (pageNumber - 1) * count;

      setState((prevState) => ({
        ...prevState,
        rows: response.data.map((row, i) => ({ ...row, sid: start + i + 1 })),
        total: response.total,
      }));
    },
    [],
    350
  );

  useEffect(() => {
    fetchRows(
      apis?.getAll,
      {
        pageSize: state.pageSize,
        pageNumber: state.pageNumber,
        sortOrder: state.sortOrder,
        sortField: state.sortField,
        search: state.search,
        tabRole: state.tabRole,
        searchForm: state.searchFormKeys,
      }
      // filterableColumns
    );
  }, [
    apis?.getAll,
    fetchRows,
    state.pageNumber,
    state.pageSize,
    state.sortField,
    state.sortOrder,
    state.search,
    state.tabRole,
    state.searchFormKeys,
  ]);

  const pagination = useMemo(
    () =>
      paginationFactory({
        onPageChange: onChangePage,
        onSizePerPageChange: onChangePageSize,
        totalSize: state.total,
        page: state.pageNumber,
        sizePerPage: state.pageSize,
        paginationSize: 5,
        pageStartIndex: 1,
        firstPageText: "First",
        prePageText: "Back",
        nextPageText: "Next",
        lastPageText: "Last",
        nextPageTitle: "First page",
        prePageTitle: "Pre page",
        firstPageTitle: "Next page",
        lastPageTitle: "Last page",
        showTotal: true,
      }),
    [onChangePage, onChangePageSize, state.pageNumber, state.pageSize, state.total]
  );

  const columnsFinal = useMemo(() => {
    const cols = [...columns];

    if (!hideSidColumn)
      cols.splice(0, 0, {
        dataField: "sid",
        text: "S.No.",
        sort: true,
      });

    if (!hideActionsColumn)
      cols.push({
        dataField: "_action",
        text: "Action",
        formatter: (_noDataColumn, row) => {
          return (
            <div className="actionIconsGroup">
              {viewLink &&
                (typeof hideViewButton === "function"
                  ? !hideViewButton(row)
                  : !hideViewButton) && (
                  <CustomTooltip tooltip={`View ${title}`}>
                    <Link
                      className="icons btn-icon font-20 text-cyanBlue"
                      to={
                        typeof viewLink === "function" ? viewLink(row) : viewLink + row.id
                      }
                    >
                      <FiEye />
                    </Link>
                  </CustomTooltip>
                )}
              {editLink &&
                (typeof hideEditButton === "function"
                  ? !hideEditButton(row)
                  : !hideEditButton) && (
                  <CustomTooltip tooltip={`Edit ${title}`}>
                    <Link
                      className="icons btn-icon font-20 text-cyanBlue"
                      to={
                        typeof editLink === "function" ? editLink(row) : editLink + row.id
                      }
                    >
                      <FiEdit3 />
                    </Link>
                  </CustomTooltip>
                )}
              {!!apis?.delete?.api &&
                (typeof hideDeleteButton === "function"
                  ? !hideDeleteButton(row)
                  : !hideDeleteButton) && (
                  <CustomTooltip tooltip={`Delete ${title}`}>
                    <span
                      onClick={(e) => handleDeleteRow(row)}
                      className="icons text-danger pointer"
                    >
                      <FaRegTrashAlt />
                    </span>
                  </CustomTooltip>
                )}
              {makeUserCondition?.(row) && (
                <CustomTooltip tooltip="Make User">
                  <span onClick={() => onClickRow(row)} className="icons text-cyanBlue">
                    <FiPlusCircle />
                  </span>
                </CustomTooltip>
              )}
              {withApproveReject && (
                <ApproveReject
                  rowId={row.id}
                  status={row.status}
                  tableName={tableName}
                  setData={handleAfterApproveReject}
                />
              )}
            </div>
          );
        },
      });

    return cols;
  }, [
    apis?.delete?.api,
    columns,
    editLink,
    handleAfterApproveReject,
    handleDeleteRow,
    hideActionsColumn,
    hideDeleteButton,
    hideEditButton,
    hideSidColumn,
    hideViewButton,
    makeUserCondition,
    onClickRow,
    tableName,
    title,
    viewLink,
    withApproveReject,
  ]);

  return (
    <ToolkitProvider
      striped
      keyField="id"
      data={state.rows}
      columns={columnsFinal}
      columnToggle
      search={{
        searchFormatted: true,
      }}
    >
      {(props) => {
        const tableExport = (
          <TableExport
            tableName={tableName || "CompleteOwnerPaymentHistory"}
            dataColumn={columnsFinal}
            renderData={state?.rows}
            searchText={props.searchProps.searchText}
            fullData={fullData}
          />
        );

        const onSearch = (q) => {
          onChangeSearch(q);
          props.searchProps.onSearch(q);
        };

        return (
          <>
            {tableBeforeSearch && tableBeforeSearch()}
            <div className="d-flex align-items-center justify-content-between pb-3">
              {searchFlag !== false && (
                <SearchBar {...props.searchProps} onSearch={onSearch} className="mb-0" />
              )}

              <div className="d-flex">
                {excelExport !== false && (
                  <CustomTooltip tooltip="Export Excel">
                    <span
                      onClick={(e) => excelExport(e)}
                      className="btn-icon text-cyanBlue"
                    >
                      <BsFileEarmarkArrowDown size="24" />
                    </span>
                  </CustomTooltip>
                )}
                {!hideAddButton && (
                  <div className="btn-toolbar">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip id="tooltip-top">Add {title}</Tooltip>}
                    >
                      <Link className="icons btn-icon font-20 text-cyanBlue" to={addLink}>
                        <FiPlusCircle />
                      </Link>
                    </OverlayTrigger>
                  </div>
                )}
                {tableKey && (
                  <>
                    <div className="d-flex hideShow">
                      <div class="d-flex hideShowColumnDiv">
                        <h3 class="hideShowColumn">Columns</h3>
                        <div style={{ color: "#ced4da" }}>
                          <IoIosArrowDropdown size={20} />
                        </div>
                      </div>
                      <CustomToggleList
                        {...props.columnToggleProps}
                        tableKey={tableKey}
                      />
                    </div>
                  </>
                )}
                <div className="btn-toolbar">
                  {tableToolbarView && tableToolbarView()}
                  {tableDownloadFlag &&
                    (downloadPermission ? (
                      <SetPermission permission={downloadPermission} data={tableExport} />
                    ) : (
                      tableExport
                    ))}
                </div>
              </div>
              {blockIpButton === true && (
                <Link to={`/global-configuration/block-ip`}>
                  <Button>Block IP</Button>
                </Link>
              )}
            </div>
            <div className={className}>
              <BootstrapTable
                {...props.baseProps}
                selectRow={selectRow || props.baseProps.selectRow || undefined}
                pagination={pagination}
                onTableChange={handleTableChange}
                noDataIndication={"Table is empty"}
                remote={{
                  pagination: true,
                  sort: true,
                }}
                bootstrap4
              />
            </div>
          </>
        );
      }}
    </ToolkitProvider>
  );
};

export default TableWithServerSide;
